/**
 * Pure draft shape + converters for the boost flow.
 *
 * Kept free of React and service imports so the regrouping logic is
 * unit-testable without the modal (same pattern as clientFormFields).
 *
 * The agent emits `platforms` in one of two shapes:
 *  - per-platform FACEBOOK/INSTAGRAM blocks — boosts of an organic post;
 *  - a single META block — the post has no Meta publish record, so the
 *    recommendation is ONE dark-post campaign spanning FB+IG.
 */

import type {
  BoostRecommendation,
  CampaignType,
  MetaPlacement,
  MetaPlatform,
  PlatformRecommendation,
  RecommendationPlatformKey,
} from "../types";

export const PLATFORM_ORDER: MetaPlatform[] = ["FACEBOOK", "INSTAGRAM"];
// Draft iteration order. "META" never coexists with per-platform blocks.
export const DRAFT_PLATFORM_ORDER: RecommendationPlatformKey[] = [
  "META",
  "FACEBOOK",
  "INSTAGRAM",
];

export interface PlatformDraft {
  enabled: boolean;
  dailyBudget: number;
  durationDays: number;
  placements: MetaPlacement[];
  reasoning?: string;
}

export const MIN_DURATION = 3;
export const MAX_DURATION = 30;

export const clampDuration = (d: number): number =>
  Math.max(MIN_DURATION, Math.min(MAX_DURATION, Math.round(d)));

export const totalFor = (block: PlatformDraft): number =>
  Math.round(block.dailyBudget * block.durationDays * 100) / 100;

export interface EditableDraft {
  campaignType: CampaignType;
  platforms: Partial<Record<RecommendationPlatformKey, PlatformDraft>>;
  ageMin: number;
  ageMax: number;
  locationTypes: string[];
  locations: Array<{
    metaId: string;
    name: string;
    radiusKm: number | null;
    countryCode?: string;
    removed: boolean;
  }>;
  interests: Array<{ metaId: string; name: string; removed: boolean }>;
  behaviors: Array<{ metaId: string; name: string; removed: boolean }>;
}

export function draftFromRecommendation(
  rec: BoostRecommendation,
): EditableDraft {
  const t = rec.targeting;
  const platforms: Partial<Record<RecommendationPlatformKey, PlatformDraft>> =
    {};
  const toDraft = (block: PlatformRecommendation): PlatformDraft => {
    // Accept both new (dailyBudget+durationDays) and legacy
    // (budget total). Legacy path divides by 7 to estimate daily.
    const duration = clampDuration(block.durationDays ?? 7);
    const daily =
      block.dailyBudget != null
        ? block.dailyBudget
        : block.budget != null
          ? Math.round((block.budget / duration) * 100) / 100
          : 0;
    return {
      enabled: block.enabled,
      dailyBudget: daily,
      durationDays: duration,
      placements: block.placements,
      reasoning: block.reasoning,
    };
  };
  const metaBlock = rec.platforms?.META;
  if (metaBlock) {
    // Single-block convention: the post has no Meta publish record, so
    // the agent recommends ONE dark-post campaign spanning FB+IG. Render
    // it as one card — per-platform FB/IG cards would misread as "not
    // published here" and hide the agent's numbers.
    platforms.META = toDraft(metaBlock);
  } else {
    // Always populate both FB and IG so the UI shows both sections —
    // platforms Claude didn't recommend render disabled but remain
    // toggle-able. Lets the user see what was considered and override.
    for (const p of PLATFORM_ORDER) {
      const block = rec.platforms?.[p];
      if (block) {
        platforms[p] = toDraft(block);
      } else {
        platforms[p] = {
          enabled: false,
          dailyBudget: 0,
          durationDays: 14,
          placements: ["FEED"],
          reasoning:
            "Agent didn't recommend this platform — likely because the post isn't published here. Enable to override, but server will reject if the post truly isn't on this platform.",
        };
      }
    }
  }
  return {
    campaignType: rec.campaignType ?? "ENGAGEMENT",
    platforms,
    ageMin: t?.ageMin ?? 18,
    ageMax: t?.ageMax ?? 65,
    locationTypes: t?.locationTypes ?? ["home"],
    locations: (t?.locations ?? []).map((l) => ({
      metaId: l.metaId,
      name: l.name,
      radiusKm: l.radiusKm ?? null,
      countryCode: l.countryCode,
      removed: false,
    })),
    interests: (t?.interests ?? []).map((i) => ({ ...i, removed: false })),
    behaviors: (t?.behaviors ?? []).map((b) => ({ ...b, removed: false })),
  };
}

export function buildManualDraft(): EditableDraft {
  return {
    campaignType: "ENGAGEMENT",
    platforms: {
      FACEBOOK: {
        enabled: true,
        dailyBudget: 5,
        durationDays: 14,
        placements: ["FEED"],
      },
      INSTAGRAM: {
        enabled: true,
        dailyBudget: 5,
        durationDays: 14,
        placements: ["FEED"],
      },
    },
    ageMin: 18,
    ageMax: 65,
    locationTypes: ["home"],
    locations: [],
    interests: [],
    behaviors: [],
  };
}

export function recommendationFromManualDraft(
  draft: EditableDraft,
): BoostRecommendation {
  const platforms: Partial<Record<MetaPlatform, PlatformRecommendation>> = {};
  for (const key of PLATFORM_ORDER) {
    const block = draft.platforms[key];
    if (!block) continue;
    platforms[key] = {
      enabled: block.enabled,
      dailyBudget: block.dailyBudget,
      durationDays: block.durationDays,
      placements: block.placements,
      reasoning: "Manual setup",
    };
  }
  return {
    decision: "recommend",
    reasoning: "Manual setup — no agent involvement",
    campaignType: draft.campaignType,
    platforms,
    targeting: {
      geographicScope: "country",
      locations: draft.locations
        .filter((l) => !l.removed)
        .map((l) => ({
          metaId: l.countryCode || l.metaId,
          name: l.name || l.countryCode || l.metaId,
          countryCode: l.countryCode,
        })),
      locationTypes: draft.locationTypes,
      ageMin: draft.ageMin,
      ageMax: draft.ageMax,
      interests: [],
      behaviors: [],
    },
  };
}

export function applyDraft(
  original: BoostRecommendation,
  draft: EditableDraft,
): BoostRecommendation {
  const t = original.targeting;
  const platforms: Partial<
    Record<RecommendationPlatformKey, PlatformRecommendation>
  > = {};
  for (const key of DRAFT_PLATFORM_ORDER) {
    const block = draft.platforms[key];
    if (!block) continue;
    platforms[key] = {
      enabled: block.enabled,
      dailyBudget: block.dailyBudget,
      durationDays: block.durationDays,
      placements: block.placements,
      reasoning: block.reasoning,
    };
  }
  return {
    ...original,
    decision: "recommend",
    campaignType: draft.campaignType,
    platforms,
    targeting: t
      ? {
          geographicScope: t.geographicScope,
          locations: draft.locations
            .filter((l) => !l.removed)
            .map((l) => ({
              metaId: l.metaId,
              name: l.name,
              radiusKm: l.radiusKm ?? undefined,
              countryCode: l.countryCode,
            })),
          locationTypes: draft.locationTypes,
          ageMin: draft.ageMin,
          ageMax: draft.ageMax,
          interests: draft.interests
            .filter((i) => !i.removed)
            .map(({ metaId, name }) => ({ metaId, name })),
          behaviors: draft.behaviors
            .filter((b) => !b.removed)
            .map(({ metaId, name }) => ({ metaId, name })),
          excludedBehaviors: t.excludedBehaviors,
        }
      : undefined,
  };
}

// ----- Execution-mode translation -------------------------------------
//
// The same recommendation can be executed three ways:
//   1. boost      — managed Meta campaign via Ads API
//   2. dark_post  — standalone ad with re-uploaded creative
//   3. manual     — user pastes the parameters into Meta Business Suite
//
// boost requires Meta's Ads API to accept the post (eligibility_by_platform).
// dark_post + manual always work as long as the user has the means.

export function recommendationToStandaloneRequest(
  postId: string,
  rec: BoostRecommendation,
) {
  // Standalone ad is ONE Meta campaign (publisherPlatforms array).
  // Sum daily budget across enabled platforms and use the longest
  // duration so the dark post matches the boost's reach intent.
  const platformsMap = rec.platforms ?? {};
  const enabled = (
    Object.keys(platformsMap) as RecommendationPlatformKey[]
  ).filter((k) => platformsMap[k]?.enabled);

  const dailyBudget = enabled.reduce(
    (sum, k) => sum + (platformsMap[k]?.dailyBudget ?? 0),
    0,
  );
  const durationDays = enabled.reduce(
    (max, k) => Math.max(max, platformsMap[k]?.durationDays ?? 0),
    7,
  );

  // A META block IS the dark-post spec: one campaign on both platforms,
  // its placements applying to each.
  const metaPlacements = platformsMap.META?.placements?.map((p) =>
    p.toLowerCase(),
  );
  const fbPositions =
    platformsMap.FACEBOOK?.placements?.map((p) => p.toLowerCase()) ??
    metaPlacements;
  const igPositions =
    platformsMap.INSTAGRAM?.placements?.map((p) => p.toLowerCase()) ??
    metaPlacements;

  const publisherPlatforms = enabled
    .map<"facebook" | "instagram" | null>((k) =>
      k === "FACEBOOK"
        ? "facebook"
        : k === "INSTAGRAM"
          ? "instagram"
          : null,
    )
    .filter((p): p is "facebook" | "instagram" => p !== null);

  return {
    postId: postId,
    caption: rec.adCopy ?? undefined,
    publisherPlatforms:
      publisherPlatforms.length > 0
        ? publisherPlatforms
        : (["facebook", "instagram"] as ("facebook" | "instagram")[]),
    facebookPositions: fbPositions,
    instagramPositions: igPositions,
    dailyBudget: dailyBudget,
    durationDays: durationDays,
    objective: rec.campaignType,
    targeting: rec.targeting!,
    agentRecommendation: rec,
  };
}

export function recommendationToClipboardText(
  rec: BoostRecommendation,
): string {
  const lines: string[] = [];
  lines.push("=== Ad parameters ===");
  if (rec.campaignType) lines.push(`Objective: ${rec.campaignType}`);
  if (rec.reasoning) lines.push(`Strategy: ${rec.reasoning}`);
  lines.push("");

  if (rec.targeting) {
    lines.push("Audience targeting:");
    const t = rec.targeting;
    lines.push(`  Geographic scope: ${t.geographicScope}`);
    if (t.locations?.length) {
      const locs = t.locations
        .map((l) =>
          l.radiusKm
            ? `${l.name} (${l.radiusKm}km)`
            : l.name,
        )
        .join(", ");
      lines.push(`  Locations: ${locs}`);
    }
    if (t.locationTypes?.length) {
      lines.push(`  Location types: ${t.locationTypes.join(", ")}`);
    }
    if (t.ageMin != null && t.ageMax != null) {
      lines.push(`  Ages: ${t.ageMin}–${t.ageMax}`);
    }
    if (t.interests?.length) {
      lines.push(`  Interests: ${t.interests.map((i) => i.name).join(", ")}`);
    }
    if (t.behaviors?.length) {
      lines.push(`  Behaviors: ${t.behaviors.map((b) => b.name).join(", ")}`);
    }
    lines.push("");
  }

  if (rec.platforms) {
    lines.push("Per-platform budget:");
    for (const [platform, block] of Object.entries(rec.platforms)) {
      if (!block?.enabled) continue;
      const total = (block.dailyBudget * block.durationDays).toFixed(2);
      const placements = block.placements?.join(", ") ?? "FEED";
      lines.push(
        `  ${platform}: €${block.dailyBudget.toFixed(2)}/day × ${block.durationDays} days = €${total} (${placements})`,
      );
    }
    lines.push("");
  }

  if (rec.adCopy) {
    lines.push("Suggested caption:");
    lines.push(rec.adCopy);
  }

  return lines.join("\n");
}
